import { useApi, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm } from '~/hooks';
import React, { useRef } from 'react';
import { Button, Buttons, CancelButton, Field, Form, FormMessage, ModalCard } from '~/components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { ErrorPage } from '~/routes/public/pages';
import { emptyStringToNull } from '~/utils';

export default function HolidayScheduleCloneConfirmation({ target: holidaySchedule, onClose, onSaved }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ status, message, isSubmitting }, form] = useForm();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  const holidayScheduleId = holidaySchedule?.id;

  async function handleSubmit(values) {
    try {
      form.submit();
      const { data } = await api.www
        .workspaces(workspace.id)
        .holidaySchedules(holidayScheduleId)
        .clone(emptyStringToNull(values));
      await onSaved(data);
      form.done();
    } catch ({ message }) {
      form.error({ message });
    }
  }

  if (!holidaySchedule) return <ErrorPage.NotFound publicSite={false} />;

  const initialValues = {
    name: `Copy of ${holidaySchedule.name}`,
    description: '',
  };

  const handleClose = () => dirtyCheck(onClose);

  return (
    <ModalCard title="Clone Holiday Schedule" onClose={handleClose}>
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          name: Yup.string().label('Name').max(255).required(),
          description: Yup.string().max(5000).nullable(),
        })}>
        {() => {
          return (
            <Form>
              <ModalCard.Body>
                <Form.Control>
                  <Field.Text autoFocus name="name" placeholder="New Name" maxLength={255} />
                </Form.Control>
                <Form.Control>
                  <Field.TextArea name="description" placeholder="Description" maxLength={5000} />
                </Form.Control>
                {status && <FormMessage.Error>{message || 'An error has occurred.'}</FormMessage.Error>}
              </ModalCard.Body>
              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={handleClose}>Close</CancelButton>
                  <Button type="submit" isLoading={isSubmitting}>
                    Save
                  </Button>
                </Buttons>
              </ModalCard.Footer>
            </Form>
          );
        }}
      </Formik>
    </ModalCard>
  );
}
